import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

// Import advanced components
import Select from 'react-select';
import Switch from 'react-switch';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

// Import CKEditor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// Import relevant Redux Thunks
import { uploadDocumentFile, saveDocumentMetadata, fetchParentCategories, fetchSubCategories } from '../../../slices/thunks';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import UploadProgressModal from '../../../Components/Common/UploadProgressModal';
import { getAuthUser } from '../../../helpers/utils';

// Register FilePond plugins
registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

// --- Reusable Quill Configuration ---

// 1. Define the modules for the toolbar
// The [{ 'align': [] }] option adds the text alignment buttons, which handle LTR and RTL text.
const quillModules = {
    toolbar: [
        [{ 'header': '1'}, { 'header': '2'}, { 'font': [] }],
        [{ 'size': [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, 
         { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }], // This is the key for RTL/LTR support
        ['link', 'image', 'video'],
        ['clean']
    ],
    clipboard: {
        matchVisual: false,
    }
};

// 2. Define the formats Quill should recognize
// It's important to whitelist the formats you've enabled in the toolbar.
const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'color', 'background', 'align' // Ensure 'align' is included
];


const AddArticle = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = getAuthUser(); 
  const isRTL = i18n.dir() === "rtl";
  const [submitAction, setSubmitAction] = useState('');
  
  // --- STATE FOR FILE UPLOADS ---
  const [uploadedPdfData, setUploadedPdfData] = useState(null);
  const [isPdfUploading, setIsPdfUploading] = useState(false);

  // <-- NEW: State for the progress log modal
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isLogModalMinimized, setIsLogModalMinimized] = useState(false);
  const [uploadLogs, setUploadLogs] = useState([]);

  const toggleLogModal = () => setIsLogModalOpen(!isLogModalOpen);
  const toggleLogModalMinimize = () => setIsLogModalMinimized(!isLogModalMinimized);

   const filePondRef = useRef(null);

  // --- SELECT DATA FROM REDUX STORE ---
  const { 
    parentCategories, 
    loading: categoriesLoading 
  } = useSelector((state) => state.Category); 
  
  const { 
    subCategories, 
    subCategoriesLoading,
    uploadProgress,
  } = useSelector((state) => state.AIDoc); 

  const handleReset = () => {
    formik.resetForm();
    // setUploadedPdfData(null);
    // toggleLogModal();
    if (filePondRef.current) {
      filePondRef.current.removeFiles();
    }
  };
  
  // --- VALIDATION SCHEMA ---
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Article name is required')
    .min(3, 'Must be more than 3 characters')
    .max(50, 'Must be 50 characters or less'),
  author: Yup.string()
    .trim()
    .required('Author name is required')
    .min(3, 'Must be more than 3 characters')
    .max(100, 'Must be 300 characters or less'),
  year: Yup.number()
    .required('Publication year is required')
    .min(1900, 'Must be after 1900')
    .max(new Date().getFullYear(), 'YearCannotBeFuture'), 
  content: Yup.string()
    .trim()
    .min(3, 'Must be more than 3 characters')
    .test(
      'is-empty',
      'Article content cannot be empty',
      (value) => {
        if (!value || value.replace(/<(.|\n)*?>/g, '').trim().length === 0) {
          return false;
        }
        return true;
      }
    )
    .required('Article content cannot be empty'),
  category: Yup.object().nullable().required('Category is required'),
  subCategory: Yup.object().nullable().required('Sub-category is required'),
  isPublic: Yup.boolean(),
  // uploadedPdfData: Yup.object().required('Please wait for the PDF to finish uploading'),
});

  const formik = useFormik({
    initialValues: {
      name: '',
      author: '',
      year: '',
      content: '',
      category: null,
      subCategory: null,
      isPublic: true,
      uploadedPdfData: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // const finalPayload = {
        //   DocumentTypeID: 6, // 6 is Article DocumentTypeID for PDF
        //   FileName: values.name,
        //   FileType: 'application/pdf', 
        //   FileURL: values.uploadedPdfData.uploadUrl,
        //   CategoryID: values.category.value,
        //   SubCategoryID: parseInt(values.subCategory.value),
        //   UniqueID: values.uploadedPdfData.uniqueId || null,
        //   IsVisible: values.isPublic,
        //   Details: JSON.stringify({
        //     name: values.name,
        //     author: values.author,
        //     year: values.year,
        //     content: values.content, 
        //   })
        // };
        
        // await dispatch(saveDocumentMetadata(finalPayload));
                
        // if (submitAction === 'addAnother') {
        //   resetForm();
        //   handleReset();
        //   setUploadedPdfData(null);
        //   window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top after adding another
        // } else {
        //   navigate(-1); 
        // }

      } catch (error) {
        console.error("Save article metadata failed:", error);
        toast.error(error.message || "Could not save article details.");
      } finally {
        setSubmitting(false);
      }
    },
  });
  
// --- UPLOAD HANDLER FOR THE PDF FILEPOND ---
const processArticlePdf = async (fieldName, file, metadata, load, error, progress, abort) => {
    // Client-side validation before starting the stream
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    if (file.type !== 'application/pdf') {
      const msg = t('InvalidFileTypePDF');
      toast.error(msg); error(msg); setIsPdfUploading(false); return;
    }
    if (file.size > MAX_FILE_SIZE) {
      const msg = t('FileSizeLimitExceeded50');
      toast.error(msg); error(msg); setIsPdfUploading(false); return;
    }

    setIsPdfUploading(true);
    setUploadLogs([]); 
    setIsLogModalOpen(true);
    setIsLogModalMinimized(false);
    
    // Use a promise to bridge SSE with FilePond's callback structure
    try {
        await new Promise((resolve, reject) => {
            const onProgress = ({ event, data }) => {
                const parsedData = typeof data === "string" ? JSON.parse(data) : data;
                // Log every message from the stream
                if(data.message) {
                  setUploadLogs(prevLogs => [...prevLogs, data.message]);
                }

                if (event === "file_ocr_done") {
                  const data = {
                    DocumentID: parsedData.DBID,
                    OriginFileName: parsedData.text?.originalFileName,   
                    Tags: parsedData.text?.tags
                  }
                  dispatch(updateDocument(data))
                  .unwrap()
                  .then(() => {
                    navigate('/ai/article');
                  })
                  .catch((error) => {
                      console.error("Metadata save failed:", error);
                  });
                }

                // ** KEY LOGIC **
                // When 'uploaded' event is received, enable the form for submission
                // if (event === "uploaded") {
                //     if (data.uploadUrl) {
                //         setUploadedPdfData(data); 
                //         setIsPdfUploading(false);
                //         formik.setFieldValue('uploadedPdfData', data);
                //     } else {
                //        const err = new Error(t("URL not received from server."));
                //        toast.error(err.message);
                //        reject(err); // Reject the promise to fail the upload in FilePond
                //     }
                // }
                
                // If the stream reports a file is a duplicate
                if (event === "duplicate") {
                    const err = new Error("duplicateFile");
                    toast.error("duplicateFile");
                    reject(err);
                    return;
                }

                // When the entire pipeline is finished, resolve the promise
                if (event === "all_done" || event === "done") {

                  toggleLogModal();
                  if (event === "done" && data.success === false) {
                    const err = new Error(data.message || "Processing failed after upload.");
                    toast.error(err.message);
                    reject(err);
                  } else {
                    setIsLogModalOpen(false)
                    toast.success(t("ArticleSavedSuccessfully"));     
                    formik.setFieldValue('uploadedPdfData', file);
                    resolve(); 
                  }
                }
            };

            const details = JSON.stringify({
              name: formik.values.name,
              author: formik.values.author,
              year: formik.values.year,
              content: formik.values.content, 
            })
            
            // Prepare FormData for the initial POST request
            const formData = new FormData();
            formData.append("file", file);
            formData.append("IsBook", false);
            formData.append("IsLargeFile", false);
            formData.append("UserID", user.ID);
            formData.append("DocumentTypeID", 6);
            formData.append("Category", formik.values.category?.Category || "");
            formData.append("SubCategory", formik.values.subCategory?.SubCategory || ""); 
            formData.append("CategoryID", formik.values.category?.value || "");
            formData.append("SubCategoryID", formik.values.subCategory?.value || "");
            formData.append("IsVisible", formik.values.isPublic || "");
            formData.append("Details", details);

            // Dispatch the thunk that handles the SSE connection
            // This thunk MUST be implemented to accept an `onProgress` callback
            dispatch(uploadDocumentFile({ formData, onProgress }));
        });

        // This code runs if the promise above resolves successfully
        // toast.success(t('Document processing pipeline finished.'));
        load(file.name); // Tell FilePond the upload is complete

    } catch (e) {
        // This code runs if the promise is rejected
        error(e.message || "An unknown error occurred during processing.");
    } finally {
        setIsPdfUploading(false);
    }

    return { abort: () => { abort(); setIsPdfUploading(false); } };
};

const handleSave = () => { setSubmitAction('save'); formik.submitForm(); };
const handleAddAnother = () => { 
  // setSubmitAction('addAnother'); 
  // formik.submitForm(); 

  formik.setFieldValue('uploadedPdfData', null);

  if (filePondRef.current) {
    filePondRef.current.removeFiles();
  }
};


  useEffect(() => {
    dispatch(fetchParentCategories({DocType: 6}));
  }, [dispatch]);

  // Fetch sub-categories when parent category changes
  useEffect(() => {
    formik.setFieldValue('subCategory', null);
    if (formik.values.category) {
      const parentId = formik.values.category.value;
      dispatch(fetchSubCategories({DocType: 6, ParentCategoryID: parentId })); 
    }
    // FIX IS HERE: The eslint-disable line was removed and setFieldValue was added to the array
  }, [formik.values.category, dispatch, formik.setFieldValue]); 

  // --- PREPARE OPTIONS FOR REACT-SELECT ---
  const parentCategoriesOptions = parentCategories.map(cat => ({
    value: cat.ID,
    label: isRTL ? cat.NameAr.trim() : cat.NameEn.trim(),
    Category: cat.NameEn?.trim() || '',
  }));

  const subCategoriesOptions = subCategories.map(cat => ({
    value: cat.ID,
    label: isRTL ? cat.CategoryNameAr.trim() : cat.CategoryNameEn.trim(),
    SubCategory: cat.CategoryNameEn?.trim() || '',
  }));

  return (
    <div className="page-content">
       <UploadProgressModal
              isOpen={isLogModalOpen}
              isMinimized={isLogModalMinimized}
              toggleMinimize={toggleLogModalMinimize}
              logs={uploadLogs}
              onClose={() => setIsLogModalOpen(false)}
              progress={uploadProgress}
              isRTL={isRTL}
        />
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col>
            <h4 className="mb-0">{t("Add New Article")}</h4>
          </Col>
          <Col xs="auto">
            <Button color="soft-secondary" disabled={isPdfUploading} onClick={() => navigate(-1)}>
              <i className={`ri-arrow-${isRTL ? "right" : "left"}-line align-middle me-1`}></i> {t("Go Back")}
            </Button>
          </Col>
        </Row>

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Main Article Details Column */}
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("Article Details")}</h5>
                </CardHeader>
                <CardBody>
                   <FormGroup className="mb-3">
                    <Label htmlFor="article-name">{t("Name of the Article")}</Label>
                    <Input
                      type="text" id="article-name" name="name" placeholder={t("Enter article name")}
                      value={formik.values.name}
                      onChange={formik.handleChange} onBlur={formik.handleBlur}
                      invalid={formik.touched.name && !!formik.errors.name}
                    />
                    {formik.touched.name && formik.errors.name && <FormFeedback>{t(formik.errors.name , { defaultValue: 'Name is required' })}</FormFeedback>}
                  </FormGroup>
                  {/* --- ENHANCED UI ROW --- */}
                  <Row>
                    <Col md={6}>
                       <FormGroup className="mb-3">
                        <Label htmlFor="author-name">{t("Author")}</Label>
                        <Input
                          type="text" id="author-name" name="author" placeholder={t("Enter author name")}
                          value={formik.values.author}
                          onChange={formik.handleChange} onBlur={formik.handleBlur}
                          invalid={formik.touched.author && !!formik.errors.author}
                        />
                        {formik.touched.author && formik.errors.author && <FormFeedback>{t(formik.errors.author , { defaultValue: 'Author is required' })}</FormFeedback>}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                       <FormGroup className="mb-3">
                        <Label htmlFor="publication-year">{t("Year")}</Label>
                        <Input
                          type="text" id="publication-year" name="year" 
                          placeholder={t("e.g") + " 2024"}
                          value={formik.values.year}
                          onChange={
                            (e) => {
                              const { value } = e.target;
                              // Allow only digits and limit to 4 characters for a year.
                              if (/^\d{0,4}$/.test(value)) {
                                formik.setFieldValue('year', value);
                              }
                            }
                          } 
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.year && !!formik.errors.year}
                        />
                        {formik.touched.year && formik.errors.year && <FormFeedback>{t(formik.errors.year , { defaultValue: 'Year is required' })}</FormFeedback>}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                       <FormGroup className="mb-3">
                        <Label htmlFor="article-category">{t("Category")}</Label>
                         <Select
                          id="article-category" name="category" options={parentCategoriesOptions}
                          isLoading={categoriesLoading} classNamePrefix="select"
                          className={formik.touched.category && formik.errors.category ? 'is-invalid' : ''}
                          value={formik.values.category}
                          onChange={option => formik.setFieldValue('category', option)}
                          onBlur={() => formik.setFieldTouched('category', true)}
                          isDisabled={ subCategoriesLoading || isPdfUploading || formik.values.uploadedPdfData}
                          placeholder={t("select")}
                        />
                        {formik.touched.category && formik.errors.category && (
                            <div className="invalid-feedback d-block">
                              {t(formik.errors.category, { defaultValue: 'Category is required' })}
                            </div>
                          )}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                       <FormGroup className="mb-3">
                        <Label htmlFor="article-subcategory">{t("Sub-category")}</Label>
                         <Select
                          id="article-subcategory" name="subCategory" options={subCategoriesOptions}
                          isLoading={subCategoriesLoading} 
                          value={formik.values.subCategory}
                          onChange={option => formik.setFieldValue('subCategory', option)}
                          onBlur={() => formik.setFieldTouched('subCategory', true)}
                          classNamePrefix="select"
                          isDisabled={ !formik.values.category || subCategoriesLoading || isPdfUploading || formik.values.uploadedPdfData}
                          className={formik.touched.subCategory && formik.errors.subCategory ? 'is-invalid' : ''}
                          placeholder={t("select")}
                        />
                        {formik.touched.subCategory && formik.errors.subCategory && (
                            <div className="invalid-feedback d-block">
                              {t(formik.errors.subCategory, { defaultValue: 'Sub-category is required' })}
                            </div>
                          )}
                        </FormGroup>
                    </Col>
                  </Row>
                  
                  <FormGroup>
                    <Label>{t("Article Content")}</Label>
                        <div 
                          className={`
                            ${isRTL ? 'quill-rtl' : ''} 
                            ${formik.touched.content && formik.errors.content ? 'quill-editor-invalid' : ''}
                          `}
                        > 
                       <ReactQuill
                          theme="snow"
                          name="content"
                          value={formik.values.content}
                          onChange={(content) => {
                              // The 'content' argument is the HTML string from the editor
                              formik.setFieldValue('content', content);
                          }}
                          onBlur={() => {
                              // Mark the field as touched for Formik validation
                              formik.setFieldTouched('content', true);
                          }}
                          modules={quillModules}
                          formats={quillFormats}
                          // placeholder={t("WriteContentHere", { defaultValue: "" })}
                        />
                     </div>
                    {formik.touched.content && formik.errors.content && (
                        <div className="invalid-feedback d-block">{t(formik.errors.content , { defaultValue: 'Content is required' })}</div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            {/* Publishing & Files Column */}
            <Col lg={4}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("Publishing & Files")}</h5>
                </CardHeader>
                <CardBody>
                   <FormGroup className="d-flex justify-content-between align-items-center mb-4">
                    <Label htmlFor="is-public-switch" className="mb-0">{t("View on Website")}</Label>
                    <Switch
                      id="is-public-switch"
                      onChange={checked => formik.setFieldValue('isPublic', checked)}
                      checked={formik.values.isPublic}
                      onColor="#0ab39c" offColor="#f06548"
                      height={24} width={48} handleDiameter={20}
                    />
                   </FormGroup>
                  <hr />
                  <FormGroup>
                  <Label>{t("Upload Article PDF")}</Label>
                  <FilePond
                    ref={filePondRef}
                    name="articlePdf" allowMultiple={false}
                    acceptedFileTypes={['application/pdf', '.pdf']}
                    server={{ process: processArticlePdf }}
                    maxFileSize={"50MB"}
                    disabled={
                      isPdfUploading ||
                      !formik.values.category ||
                      !formik.values.subCategory
                    }
                    labelIdle={`${t("Drag & Drop PDF or")} <span class="filepond--label-action">(${t("Browse")})</span>`}
                    labelFileProcessing={t('filepondProcessing', { defaultValue: 'Uploading' })}
                    labelFileProcessingComplete={t('filepondComplete', { defaultValue: 'Upload complete' })}
                    labelFileProcessingAborted={t('filepondAborted', { defaultValue: 'Upload cancelled' })}
                    labelFileProcessingError={t('filepondError', { defaultValue: 'Error during upload' })}
                    labelTapToCancel={t('filepondTapToCancel', { defaultValue: 'tap to cancel' })}
                    labelTapToRetry={t('filepondTapToRetry', { defaultValue: 'tap to retry' })}
                  />
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row className="mt-3">
            <Col className="d-flex align-items-center">
              {/* <Button type="button" color="primary" onClick={handleSave} 
                disabled={!formik.isValid || !formik.values.uploadedPdfData || formik.isSubmitting || isPdfUploading}>
                {formik.isSubmitting ? t('Saving') : t('Save Article')}
              </Button> */}
              <Button type="button" color="success" className="ms-2 me-2" onClick={handleAddAnother} 
                  disabled={!formik.isValid || !formik.values.uploadedPdfData || formik.isSubmitting || isPdfUploading}>
                 {t("Save & Add Another")}
              </Button>
              <Button type="button" color="light"  onClick={handleReset} 
              disabled={formik.isSubmitting || isPdfUploading }
              >
                {t("Reset")}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddArticle;  